/**
 * @quotech/ui - Universal Profile Settings Tab
 *
 * PATH: packages/ui/src/components/settings/ProfileTab.tsx
 * PURPOSE: Orchestrator for profile display/edit — delegates to sub-components
 * WHY: Hub-agnostic profile editor; receives all data/callbacks via props
 *
 * FLOW:
 * ┌────────────┐    ┌───────────────┐    ┌──────────┐
 * │ ProfileData│ →  │ ProfileTab    │ →  │ onSave() │
 * └────────────┘    │ ├ Avatar      │    └──────────┘
 *                   │ ├ FormInputs  │
 *                   │ └ Actions     │
 *                   └───────────────┘
 *
 * DEPENDENCIES:
 * - profile-tab/profile-tab-types: shared types
 * - profile-tab/AvatarUploader: avatar display/upload/remove
 * - profile-tab/FormInput: labelled input with icon + error
 */

import { useState, useCallback } from 'react'
import { User, Mail, Phone, MapPin, Loader2, Check, X } from 'lucide-react'
import { cn } from '../../lib/utils'

import type { ProfileData, ProfileTabProps } from './profile-tab/profile-tab-types'
import { AvatarUploader } from './profile-tab/AvatarUploader'
import { FormInput } from './profile-tab/FormInput'

// Re-export types so existing consumers are unaffected
export type { ProfileData, ProfileTabProps } from './profile-tab/profile-tab-types'

// =============================================================================
// HELPERS
// =============================================================================

/** Build a blank form state from profile data, defaulting empty strings */
function buildFormState(profile: ProfileData): ProfileData {
  return {
    first_name: profile.first_name || '',
    last_name: profile.last_name || '',
    email: profile.email || '',
    phone: profile.phone || '',
    country: profile.country || '',
    state: profile.state || '',
    city: profile.city || '',
  }
}

/** Validate required fields, return error map (empty = valid) */
function validateProfile(data: ProfileData): Record<string, string> {
  const errors: Record<string, string> = {}
  if (!data.first_name?.trim()) {
    errors.first_name = 'First name is required'
  }
  if (!data.email?.trim()) {
    errors.email = 'Email is required'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.email = 'Invalid email format'
  }
  return errors
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function ProfileTab({
  profile,
  isLoading = false,
  isSaving = false,
  onSave,
  onAvatarChange,
  onAvatarRemove,
  editable = true,
  className,
}: ProfileTabProps) {
  const [formData, setFormData] = useState<ProfileData>(() => buildFormState(profile))
  const [hasChanges, setHasChanges] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleChange = useCallback((name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }))
    setHasChanges(true)
    if (errors[name]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[name]
        return next
      })
    }
  }, [errors])

  const handleCancel = useCallback(() => {
    setFormData(buildFormState(profile))
    setHasChanges(false)
    setErrors({})
  }, [profile])

  const handleSave = useCallback(async () => {
    if (!onSave) return

    const newErrors = validateProfile(formData)
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    try {
      await onSave(formData)
      setHasChanges(false)
    } catch (err) {
      if (import.meta.env.DEV) console.error('Failed to save profile:', err)
    }
  }, [formData, onSave])

  // ── Loading state ──────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
      </div>
    )
  }

  const fieldDisabled = !editable || isSaving

  return (
    <div className={cn('space-y-8', className)}>
      {/* Avatar Section */}
      <AvatarUploader
        profile={profile}
        editable={editable}
        onAvatarChange={onAvatarChange}
        onAvatarRemove={onAvatarRemove}
      />

      {/* Form Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormInput
          label="First Name"
          name="first_name"
          value={formData.first_name || ''}
          onChange={handleChange}
          placeholder="Enter first name"
          disabled={fieldDisabled}
          icon={<User className="w-4 h-4" />}
          error={errors.first_name}
        />
        <FormInput
          label="Last Name"
          name="last_name"
          value={formData.last_name || ''}
          onChange={handleChange}
          placeholder="Enter last name"
          disabled={fieldDisabled}
          icon={<User className="w-4 h-4" />}
          error={errors.last_name}
        />
        <FormInput
          label="Email"
          name="email"
          type="email"
          value={formData.email || ''}
          onChange={handleChange}
          placeholder="Enter email"
          disabled={fieldDisabled}
          icon={<Mail className="w-4 h-4" />}
          error={errors.email}
        />
        <FormInput
          label="Phone"
          name="phone"
          type="tel"
          value={formData.phone || ''}
          onChange={handleChange}
          placeholder="Enter phone number"
          disabled={fieldDisabled}
          icon={<Phone className="w-4 h-4" />}
        />
        <FormInput
          label="Country"
          name="country"
          value={formData.country || ''}
          onChange={handleChange}
          placeholder="Enter country"
          disabled={fieldDisabled}
          icon={<MapPin className="w-4 h-4" />}
        />
        <FormInput
          label="State"
          name="state"
          value={formData.state || ''}
          onChange={handleChange}
          placeholder="Enter state"
          disabled={fieldDisabled}
          icon={<MapPin className="w-4 h-4" />}
        />
        <FormInput
          label="City"
          name="city"
          value={formData.city || ''}
          onChange={handleChange}
          placeholder="Enter city"
          disabled={fieldDisabled}
          icon={<MapPin className="w-4 h-4" />}
        />
      </div>

      {/* Actions */}
      {editable && onSave && hasChanges && (
        <div className="flex items-center gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              'bg-teal-500 text-white hover:bg-teal-600',
              isSaving && 'opacity-60 cursor-not-allowed'
            )}
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            Save Changes
          </button>

          <button
            onClick={handleCancel}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        </div>
      )}
    </div>
  )
}

export default ProfileTab
